import { useState } from 'react';
import { useOutletContext } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Typography from '@mui/material/Typography';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { graphql } from '../../../gql';
import type { AssetMaintenanceQuery } from '../../../gql/graphql';
import { DataGrid } from '../../../components/data-grid';
import { LoadingOverlay, SectionHeading, displayBoolean, displayDate, displayInteger, displayText } from '../../../components/ui';
import type { AssetDetailFragment } from '../AssetDetailPage';

const ASSET_MAINTENANCE_QUERY = graphql(`
  query AssetMaintenance($assetId: Int!) {
    assetMaintenance(assetId: $assetId) {
      latestSmr cumulativeSmr nextServiceDueInHours
      configuration {
        startingSmr isStatusActive firstReminderInHours overdueReminderInHours serviceTypes
      }
      tasks {
        id serviceType assetHours isCompleted serviceDate smrAtTimeOfService completedBy
      }
    }
  }
`);

type MaintenanceTask = NonNullable<AssetMaintenanceQuery['assetMaintenance']>['tasks'][number];

const columns: ColDef<MaintenanceTask>[] = [
  { field: 'serviceType', headerName: 'Service Type', flex: 2, valueFormatter: (params) => displayText(params.value) },
  { field: 'assetHours', headerName: 'Asset Hours', flex: 1, valueFormatter: (params) => displayInteger(params.value) },
  { field: 'isCompleted', headerName: 'Completed', flex: 1, cellRenderer: (params: ICellRendererParams<MaintenanceTask>) => displayBoolean(params.value) },
  { field: 'serviceDate', headerName: 'Service Date', flex: 1, valueFormatter: (params) => displayDate(params.value as string | null) },
  { field: 'smrAtTimeOfService', headerName: 'SMR at Time of Service', flex: 2, valueFormatter: (params) => displayInteger(params.value) },
  { field: 'completedBy', headerName: 'Completed By', flex: 2, valueFormatter: (params) => displayText(params.value) },
];

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-divider py-2 last:border-0">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" className="text-right">{value}</Typography>
    </div>
  );
}

export function AssetMaintenanceTab() {
  const asset = useOutletContext<AssetDetailFragment | null>();
  const [showAll, setShowAll] = useState(false);
  const { data, loading, error, refetch } = useQuery(ASSET_MAINTENANCE_QUERY, {
    variables: { assetId: asset?.id ?? 0 },
    skip: asset == null,
  });
  const maintenance = data?.assetMaintenance;
  const configuration = maintenance?.configuration;
  const tasks = maintenance?.tasks ?? [];

  return (
    <section className="py-6" aria-label="Maintenance">
      <LoadingOverlay visible={loading} />
      {error ? (
        <div role="alert">
          <Typography color="error">{error.message}</Typography>
          <Button onClick={() => void refetch()}>Retry</Button>
        </div>
      ) : maintenance && (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(250px,1fr)_minmax(0,2fr)]">
          <div className="space-y-6">
            <div>
              <SectionHeading>Utilization data</SectionHeading>
              {configuration == null && <Typography variant="body2">No maintenance configuration.</Typography>}
              <Field label="Starting SMR" value={displayInteger(configuration?.startingSmr)} />
              <Field label="Latest SMR" value={displayInteger(maintenance.latestSmr)} />
              <Field label="Cumulative SMR" value={displayInteger(maintenance.cumulativeSmr)} />
              <Field label="Next service due in hours" value={displayInteger(maintenance.nextServiceDueInHours)} />
            </div>
            <div>
              <SectionHeading>Service types</SectionHeading>
              <Typography variant="body2">{configuration?.serviceTypes.length ? configuration.serviceTypes.join(', ') : '-'}</Typography>
            </div>
            <div>
              <SectionHeading>Reminders</SectionHeading>
              <Field label="Status" value={configuration == null ? '-' : configuration.isStatusActive ? 'Active' : 'Inactive'} />
              <Field label="First reminder (hours)" value={displayInteger(configuration?.firstReminderInHours)} />
              <Field label="Overdue reminder (hours)" value={displayInteger(configuration?.overdueReminderInHours)} />
            </div>
          </div>
          <div className="min-w-0">
            <div className="mb-3 flex items-center justify-between gap-3">
              <SectionHeading>Maintenance tasks</SectionHeading>
              {tasks.length > 4 && (
                <ButtonGroup size="small" aria-label="Task count">
                  <Button variant={showAll ? 'outlined' : 'contained'} onClick={() => setShowAll(false)}>Last 4</Button>
                  <Button variant={showAll ? 'contained' : 'outlined'} onClick={() => setShowAll(true)}>All</Button>
                </ButtonGroup>
              )}
            </div>
            {tasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No maintenance tasks.</Typography>
            ) : (
              <DataGrid
                rowData={showAll ? tasks : tasks.slice(0, 4)}
                columnDefs={columns}
                getRowId={(params) => String(params.data.id)}
                height={Math.min(showAll ? tasks.length : 4, 6) * 42 + 52}
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}